import { Radio, RadioTower } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import type { BlueiotStatus } from '@/types/liveview'

/**
 * Whether the API is actually reading the venue's BlueIOT relay.
 *
 * Next to `ConnectionStatus` because the two fail for entirely different
 * reasons and look identical from the map: a live websocket with a dead relay
 * is a map that is confidently showing nothing. Rendered only for an
 * organisation that has a relay configured — the heartbeat omits the block for
 * everyone else, so there is nothing to explain to them.
 */
export function RelayStatus({ status }: { status: BlueiotStatus | null }) {
  if (!status) return null

  const Icon = status.connected ? RadioTower : Radio
  const unmapped = status.unmapped_floor > 0

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Badge
          variant={status.connected ? (unmapped ? 'warning' : 'secondary') : 'destructive'}
          className="gap-1.5 px-2 py-1"
        >
          <Icon />
          {status.connected ? status.tags : 'Relay'}
        </Badge>
      </TooltipTrigger>
      <TooltipContent>
        {status.connected ? (
          <>
            BlueIOT relay connected · {status.tags} {status.tags === 1 ? 'tag' : 'tags'}
            {unmapped && (
              <>
                <br />
                {status.unmapped_floor} on a floor this venue&apos;s ground floor number cannot map
              </>
            )}
          </>
        ) : (
          'BlueIOT relay not connected — tags will stop updating'
        )}
      </TooltipContent>
    </Tooltip>
  )
}
